import { fetchServer } from "@/features/utils/fetch-server";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface PublicConversation {
  public_id: string;
  /** Wrap-up title, or the visitor's opening question. Can be empty on a fresh thread. */
  name: string;
  /** Last thing said in the thread, either side. */
  preview: string;
  created_at: number;
  updated_at: number;
  state: "open" | "closed";
  is_current: boolean;
}

export const publicConversationKeys = {
  all: ["public-conversations"] as const,
  list: (publicId: string) =>
    [...publicConversationKeys.all, publicId] as const,
};

/**
 * The visitor's past web-chat threads. Only fetched while the list is on screen:
 * the widget opens on the conversation, not on a history nobody asked for.
 */
export function usePublicConversations(publicId: string, enabled: boolean) {
  return useQuery({
    queryKey: publicConversationKeys.list(publicId),
    queryFn: async (): Promise<PublicConversation[]> => {
      const response = await fetchServer(
        `/api/sessions/v1/public/${publicId}/conversations`,
        { credentials: "include" },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch conversations");
      }

      return response.json();
    },
    enabled: !!publicId && enabled,
    // The list is a jumping-off point, not live data — but a thread the visitor just
    // left should show its new last message when they come back to it.
    staleTime: 10 * 1000,
  });
}

/** Open a fresh thread next to the one `publicId` names. */
export function useStartNewConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (publicId: string): Promise<{ public_id: string }> => {
      const response = await fetchServer(
        `/api/sessions/v1/public/${publicId}/conversations`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to start a new conversation");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicConversationKeys.all });
    },
  });
}
